#!/usr/bin/env node

import 'dotenv/config'
import * as path from 'node:path'
import { fileURLToPath } from 'node:url'
import { addSkillToConfig, removePawFromConfig, removeSkillFromConfig } from './config/index.js'
import { createLogger, setLoggerSilent } from './core/logger.js'
import { createEngine } from './index.js'

const logger = createLogger('cli')

async function main(): Promise<void> {
	const args = process.argv.slice(2)
	const command = args[0]

	const projectRoot = process.cwd()

	// Show CLI output on the console. The noisy agent loop only runs in the __run-agent daemon
	// (whose stdout is ignored), so foreground commands can log freely.
	setLoggerSilent(false)

	// The single-engine workflow was removed — OpenVole now runs as a server (agents).
	if (command === 'start' || command === 'run' || command === 'init') {
		logger.error(`"vole ${command}" has been removed — OpenVole now runs as a server.`)
		logger.info(
			'Run "vole serve" to manage agents in the dashboard, or "vole agent create <name>" to add one.',
		)
		process.exit(1)
	}

	// These commands don't operate on a cwd project; everything else needs a vole.config.json.
	if (
		command !== 'help' &&
		command !== '--help' &&
		command !== '-h' &&
		command !== '--version' &&
		command !== '-v' &&
		command !== 'upgrade' &&
		command !== 'agent' &&
		command !== 'space' && // deprecated alias for 'agent'
		command !== '__run-agent' &&
		command !== '__run-space' && // deprecated alias
		command !== 'serve' &&
		command !== undefined
	) {
		const fsCheck = await import('node:fs/promises')
		try {
			await fsCheck.access(path.join(projectRoot, 'vole.config.json'))
		} catch {
			logger.error('vole.config.json not found in current directory')
			logger.info('cd into an agent directory, or run "vole serve" to manage agents')
			process.exit(1)
		}
	}

	switch (command) {
		case 'paw':
			await handlePawCommand(args.slice(1), projectRoot)
			break

		case 'skill':
			await handleSkillCommand(args.slice(1), projectRoot)
			break

		case 'tool':
			await handleToolCommand(args.slice(1), projectRoot)
			break

		case 'project':
			await handleProjectCommand(args.slice(1), projectRoot)
			break

		case 'task':
			await handleTaskCommand(args.slice(1), projectRoot)
			break

		case 'clawhub':
			await handleClawHubCommand(args.slice(1), projectRoot)
			break

		case 'net':
			await handleNetCommand(args.slice(1), projectRoot)
			break

		case 'serve':
			await runServe(projectRoot)
			break

		case 'agent':
			await handleAgentCommand(args.slice(1))
			break

		case 'space':
			logger.warn('`vole space` is deprecated — use `vole agent`.')
			await handleAgentCommand(args.slice(1))
			break

		// Old entry name kept as an alias so anything spawning it keeps working.
		case '__run-space':
		case '__run-agent': {
			const agentPath = args[1]
			if (!agentPath) {
				logger.error('Usage: vole __run-agent <dir>')
				process.exit(1)
			}
			await runAgentDaemon(path.resolve(agentPath))
			break
		}

		case 'upgrade':
			await handleUpgrade(projectRoot)
			break

		case undefined:
		case 'help':
		case '--help':
		case '-h':
			printHelp()
			break

		case '--version':
		case '-v': {
			const { readFile } = await import('node:fs/promises')
			const pkg = JSON.parse(await readFile(new URL('../package.json', import.meta.url), 'utf-8'))
			logger.info(`openvole v${pkg.version}`)
			break
		}

		default:
			logger.error(`Unknown command: ${command}`)
			printHelp()
			process.exit(1)
	}
}

function printHelp(): void {
	logger.info(`
OpenVole — Micro Agent Core

Usage:
  vole serve                             Start the control-plane dashboard — manage all agents (main entrypoint)
  vole upgrade                           Upgrade openvole packages — every agent when run at a server root, otherwise the current agent

Paw management:
  vole paw create <name>                 Scaffold a new Paw in paws/
  vole paw list                          List loaded Paws and their tools
  vole paw add <name>                    Install and register a Paw
  vole paw remove <name>                 Uninstall and deregister a Paw

Skill management:
  vole skill create <name>               Scaffold a new Skill in skills/
  vole skill list                        List Skills and activation status
  vole skill add <name>                  Install and register a Skill
  vole skill remove <name>              Uninstall and deregister a Skill
  vole skill search <query>              Search VoleHub for skills
  vole skill install <name>              Install a skill from VoleHub
  vole skill uninstall <name>            Remove a VoleHub skill
  vole skill publish [path]              Prepare a skill for VoleHub publishing
  vole skill hub                         List installed VoleHub skills

Projects & tasks (what the agent is working on):
  vole project list [--all]              List projects in this agent's workspace
  vole project scan <path>               Inspect a directory before making it a project (read-only)
  vole project create <id> [--name <n>] [--kind <k>] [--root <path>]
  vole project open <id>                 Show a project, its context docs and open tasks
  vole project archive <id>              Retire a project (keeps all files and history)
  vole task list [projectId] [--state <s>]         List work items (default: open)
  vole task add <projectId> <goal...> [--criteria "..."] [--priority <n>]
  vole task next [projectId]             Show the next queued task
  vole task update <projectId> <taskId> --state <s> [--note "..."]
  vole task cancel <projectId> <taskId>  Cancel a task

Tool management:
  vole tool list                         List all registered tools (from manifests)
  vole tool list --live                  List all tools (boots engine, discovers MCP)
  vole tool call <name> [json-params]    Call a tool directly (deterministic, no Brain)

ClawHub (OpenClaw skill registry):
  vole clawhub install <skill>           Install a skill from ClawHub
  vole clawhub remove <skill>            Remove a ClawHub-installed skill
  vole clawhub search <query>            Search for skills on ClawHub

VoleNet (distributed networking):
  vole net init [name]                   Generate Ed25519 keypair
  vole net show-key                      Display public key (share with peers)
  vole net trust <key>                   Trust a peer's public key
  vole net join <url>                    Join a public hub (register, trust, add as peer)
  vole net pair <url>                    Pair with a node you operate (fingerprint + their consent)
  vole net pair list|accept|deny [ref]   Respond to inbound pair requests
  vole net send <file> --to <peer> --agent <name>   Send a file E2E-encrypted (VoleDrop; --wait to follow)
  vole net revoke <id>                   Remove trust for a peer
  vole net peers                         List trusted peers
  vole net status                        Network status overview

Agent management ("vole space" is a deprecated alias):
  vole serve                             Control-plane dashboard for this dir's agents (empty dir = new root; VOLE_HOME overrides)
  vole agent create <name>               Scaffold a new agent (clones your template if set; --orchestrator to let it manage siblings)
  vole agent rename <name> <new name>    Rename an agent's display name (its id and directory stay put)
  vole agent orchestrate <name> [on|off] Grant/revoke an agent's sibling-management authority (vole serve)
  vole agent template                    Create/locate the template new agents clone
  vole agent list                        List agents and running status
  vole agent start <name>                Start an agent's engine (lazy, own process)
  vole agent stop <name> | --all         Stop an agent (or all agents)
  vole agent status [name]               Show live status (pid, port)
  vole agent switch <name>               Set the active agent
  vole agent remove <name> [--purge]     Remove an agent (--purge deletes files)

Task management:
  vole task list                         Show task queue
  vole task cancel <id>                  Cancel a task

Upgrade:
  vole upgrade                           Upgrade core + all installed @openvole/* paws

Options:
  -h, --help                             Show this help
  -v, --version                          Show version
`)
}

interface UpgradeResult {
	/** Lines like "@openvole/paw-session: ^2.3.0 → ^2.3.2". */
	upgraded: string[]
	unchanged: string[]
	/** Scaffolding notes — new files written, prompts left alone. */
	notes: string[]
	/** Set when this directory could not be upgraded at all. */
	error?: string
}

/**
 * Upgrade every openvole package in one agent directory.
 *
 * Returns what changed instead of exiting, so a whole server can be walked and summarised —
 * one agent failing to resolve must not abandon the rest.
 */
async function upgradeAgentDir(projectRoot: string): Promise<UpgradeResult> {
	const fs = await import('node:fs/promises')
	const { execa: execaFn } = await import('execa')

	// Read package.json to find openvole packages
	const pkgPath = path.join(projectRoot, 'package.json')
	let pkgJson: Record<string, unknown>
	try {
		pkgJson = JSON.parse(await fs.readFile(pkgPath, 'utf-8'))
	} catch {
		return { upgraded: [], unchanged: [], notes: [], error: 'no package.json' }
	}

	const deps = (pkgJson.dependencies ?? {}) as Record<string, string>
	const devDeps = (pkgJson.devDependencies ?? {}) as Record<string, string>
	const allDeps = { ...deps, ...devDeps }

	// Collect openvole and @openvole/* packages
	const packages = Object.keys(allDeps).filter(
		(name) => name === 'openvole' || name.startsWith('@openvole/'),
	)

	if (packages.length === 0) {
		// Still refresh skills: they come from VoleHub, not npm, so "no openvole packages here"
		// says nothing about whether this agent's skills are current.
		return { upgraded: [], unchanged: [], notes: await upgradeSkills(projectRoot) }
	}

	// Record versions before upgrade
	const beforeVersions: Record<string, string> = {}
	for (const pkg of packages) {
		beforeVersions[pkg] = allDeps[pkg]
	}

	// Install all packages @latest in a single command to resolve peer deps together
	const installArgs = packages.map((pkg) => `${pkg}@latest`)
	try {
		await execaFn('npm', ['install', ...installArgs], { cwd: projectRoot, stdio: 'inherit' })
	} catch (err) {
		return {
			upgraded: [],
			unchanged: [],
			notes: [],
			error: `npm install failed: ${err instanceof Error ? err.message.split('\n')[0] : err}`,
		}
	}

	// Ensure paw data directories exist and scaffold BRAIN.md for brain paws
	const notes: string[] = []
	const pawPackages = packages.filter((p) => p.startsWith('@openvole/paw-'))
	for (const pkg of pawPackages) {
		const pawName = pkg.replace('@openvole/', '')
		const pawDataDir = path.join(projectRoot, '.openvole', 'paws', pawName)
		await fs.mkdir(pawDataDir, { recursive: true })

		// Scaffold BRAIN.md for brain paws.
		//
		// BRAIN.md is the agent's system prompt and the most likely file to have been hand-tuned,
		// so an upgrade must never overwrite one that exists. It used to move the local copy to
		// BRAIN.md.old and write the package version over it — survivable for one agent, but this
		// command now walks a whole server, which would replace every customized prompt at once.
		// A changed default is offered alongside instead, and the running prompt is left alone.
		const pkgBrainPath = path.join(projectRoot, 'node_modules', pkg, 'BRAIN.md')
		try {
			const brainContent = await fs.readFile(pkgBrainPath, 'utf-8')
			if (brainContent.trim()) {
				const localBrainPath = path.join(pawDataDir, 'BRAIN.md')
				let existing: string | null = null
				try {
					existing = await fs.readFile(localBrainPath, 'utf-8')
				} catch {
					/* none yet */
				}
				if (existing === null) {
					await fs.writeFile(localBrainPath, brainContent, 'utf-8')
					notes.push(`scaffolded ${pawName}/BRAIN.md`)
				} else if (existing !== brainContent) {
					const distPath = path.join(pawDataDir, 'BRAIN.md.dist')
					await fs.writeFile(distPath, brainContent, 'utf-8')
					notes.push(`${pawName}/BRAIN.md kept (yours); new default written to BRAIN.md.dist`)
				}
			}
		} catch {
			// No BRAIN.md in package — not a brain paw
		}
	}

	// Re-read package.json to detect version changes
	const updatedPkgJson = JSON.parse(await fs.readFile(pkgPath, 'utf-8'))
	const updatedDeps = {
		...(updatedPkgJson.dependencies ?? {}),
		...(updatedPkgJson.devDependencies ?? {}),
	} as Record<string, string>

	const upgraded: string[] = []
	const unchanged: string[] = []

	for (const pkg of packages) {
		const before = beforeVersions[pkg]
		const after = updatedDeps[pkg]
		if (before !== after) {
			upgraded.push(`${pkg}: ${before} → ${after}`)
		} else {
			unchanged.push(pkg)
		}
	}

	notes.push(...(await upgradeSkills(projectRoot)))

	return { upgraded, unchanged, notes }
}

/** `version:` from a SKILL.md frontmatter block, or null. */
async function skillVersion(skillMdPath: string): Promise<string | null> {
	const fs = await import('node:fs/promises')
	try {
		const head = (await fs.readFile(skillMdPath, 'utf-8')).slice(0, 2000)
		return head.match(/^version:\s*["']?([^"'\s]+)/m)?.[1] ?? null
	} catch {
		return null
	}
}

/**
 * Bring VoleHub skills up to date alongside the paws.
 *
 * Skills come from a different registry than paws — files fetched from VoleHub rather than npm
 * packages — so `npm install` never touched them and an agent could sit on a year-old skill with
 * nothing to say so. Paws had this fixed; skills did not.
 *
 * Only skills under `skills/volehub/` are rewritten, because those are ours to manage: the
 * installer put them there and the config names them `volehub/<name>`. A skill sitting directly in
 * `skills/<name>` is somebody's own work — hand-authored, or a copy deliberately edited — and
 * overwriting it would be the `BRAIN.md` mistake again. Those are reported when the registry has a
 * newer release and left exactly as they are.
 *
 * A registry that cannot be reached is not an upgrade failure. The paws still upgraded.
 */
async function upgradeSkills(projectRoot: string): Promise<string[]> {
	const fs = await import('node:fs/promises')
	const notes: string[] = []
	const skillsRoot = path.join(projectRoot, '.openvole', 'skills')

	let entries: string[]
	try {
		entries = (await fs.readdir(skillsRoot, { withFileTypes: true }))
			.filter((e) => e.isDirectory())
			.map((e) => e.name)
	} catch {
		return notes
	}

	const managedDir = path.join(skillsRoot, 'volehub')
	let managed: string[] = []
	try {
		managed = (await fs.readdir(managedDir, { withFileTypes: true }))
			.filter((e) => e.isDirectory())
			.map((e) => e.name)
	} catch {
		/* none installed from the hub */
	}
	const local = entries.filter((n) => n !== 'volehub' && n !== 'clawhub')
	if (managed.length === 0 && local.length === 0) return notes

	const { VoleHubClient, isOlder } = await import('./skill/volehub.js')
	const hub = new VoleHubClient()
	let index: Awaited<ReturnType<typeof hub.fetchIndex>>
	try {
		index = await hub.fetchIndex()
	} catch (err) {
		notes.push(
			`skills not checked — VoleHub unreachable (${err instanceof Error ? err.message : err})`,
		)
		return notes
	}
	const available = new Map((index.skills ?? []).map((s) => [s.name, s.version]))

	for (const name of managed) {
		const latest = available.get(name)
		if (!latest) continue
		const have = await skillVersion(path.join(managedDir, name, 'SKILL.md'))
		if (have && !isOlder(have, latest)) continue
		try {
			await hub.install(name, projectRoot)
			notes.push(`skill ${name}: ${have ?? 'unknown'} → ${latest}`)
		} catch (err) {
			notes.push(`skill ${name} failed to upgrade: ${err instanceof Error ? err.message : err}`)
		}
	}

	for (const name of local) {
		const latest = available.get(name)
		if (!latest) continue
		const have = await skillVersion(path.join(skillsRoot, name, 'SKILL.md'))
		if (have && isOlder(have, latest)) {
			notes.push(
				`skill ${name} is ${have}, VoleHub has ${latest} — kept (yours, in skills/${name}). ` +
					`Run "vole skill install ${name}" to take the published copy.`,
			)
		}
	}

	return notes
}

/**
 * `vole upgrade` — one agent, or every agent on a server.
 *
 * Run inside an agent directory it upgrades that agent. Run at a vole server root (the directory
 * holding `agents.json`) it walks every registered agent instead.
 *
 * The server form exists because paws are installed per agent: a fix published to npm reaches an
 * agent only when someone upgrades *that* directory, so on a multi-agent server the natural
 * outcome was agents silently sitting on old paws — and a stale paw looks like a live bug, not a
 * missed upgrade.
 */
async function handleUpgrade(projectRoot: string): Promise<void> {
	const fs = await import('node:fs/promises')

	const registryPath = await findAgentRegistry(projectRoot)
	if (!registryPath) {
		// A plain agent directory — upgrade it and report as before.
		const result = await upgradeAgentDir(projectRoot)
		if (result.error) {
			logger.error(
				result.error === 'no package.json'
					? 'package.json not found — run "npm init" first'
					: result.error,
			)
			process.exit(1)
		}
		if (result.upgraded.length > 0) {
			logger.info('\nUpgraded:')
			for (const line of result.upgraded) logger.info(`  ${line}`)
		}
		for (const note of result.notes) logger.info(`  ${note}`)
		if (result.unchanged.length > 0) logger.info(`\nAlready latest: ${result.unchanged.join(', ')}`)
		if (result.upgraded.length === 0) logger.info('\nAll packages are already up to date.')
		return
	}

	const registry = JSON.parse(await fs.readFile(registryPath, 'utf-8')) as {
		agents?: Array<{ id: string; name: string; path: string }>
		spaces?: Array<{ id: string; name: string; path: string }>
	}
	const agents = registry.agents ?? registry.spaces ?? []
	if (agents.length === 0) {
		logger.info('No agents registered on this server.')
		return
	}

	logger.info(`Upgrading ${agents.length} agent(s) on this server…\n`)

	const changed: string[] = []
	const current: string[] = []
	const failed: string[] = []

	for (const agent of agents) {
		logger.info(`── ${agent.name || agent.id}`)
		const result = await upgradeAgentDir(agent.path)
		if (result.error) {
			logger.error(`   ${result.error}`)
			failed.push(`${agent.name || agent.id} (${result.error})`)
			continue
		}
		for (const note of result.notes) logger.info(`   ${note}`)
		if (result.upgraded.length > 0) {
			for (const line of result.upgraded) logger.info(`   ${line}`)
			changed.push(agent.name || agent.id)
		} else {
			logger.info('   already up to date')
			current.push(agent.name || agent.id)
		}
	}

	logger.info('')
	if (changed.length > 0) {
		logger.info(`Upgraded: ${changed.join(', ')}`)
		// Paws are loaded when an engine starts, so nothing changes for a running agent until it
		// is restarted — which is exactly how an upgrade appears to have done nothing.
		logger.info('Restart the server for running agents to pick these up.')
	}
	if (current.length > 0) logger.info(`Already latest: ${current.join(', ')}`)
	if (failed.length > 0) logger.error(`Failed: ${failed.join(', ')}`)
}

/**
 * The agent registry governing this directory, if any.
 *
 * Checked in the order that matches intent: the directory you are standing in, then VOLE_HOME,
 * so `vole upgrade` at a server root always means "this server" even when VOLE_HOME points
 * somewhere else.
 */
export async function findAgentRegistry(cwd: string): Promise<string | null> {
	const fs = await import('node:fs/promises')
	const os = await import('node:os')

	const candidates = [cwd]
	if (process.env.VOLE_HOME) candidates.push(process.env.VOLE_HOME)
	else candidates.push(path.join(os.homedir(), '.openvole'))

	for (const dir of candidates) {
		// An agent directory wins over any registry: standing inside one means that agent.
		try {
			await fs.access(path.join(dir, 'vole.config.json'))
			return null
		} catch {
			/* not an agent dir */
		}
		for (const name of ['agents.json', 'spaces.json']) {
			try {
				await fs.access(path.join(dir, name))
				return path.join(dir, name)
			} catch {
				/* keep looking */
			}
		}
	}
	return null
}

async function handlePawCommand(args: string[], projectRoot: string): Promise<void> {
	const subcommand = args[0]

	switch (subcommand) {
		case 'create': {
			const name = args[1]
			if (!name) {
				logger.error('Usage: vole paw create <name>')
				process.exit(1)
			}
			await scaffoldPaw(projectRoot, name)
			break
		}

		case 'list': {
			// Lightweight — read manifests without spawning paws
			const config = await (await import('./config/index.js')).loadConfig(
				path.resolve(projectRoot, 'vole.config.json'),
			)
			const { normalizePawConfig } = await import('./config/index.js')
			const { readPawManifest, resolvePawPath } = await import('./paw/manifest.js')

			const paws: Array<{ name: string; tools: number; type: string }> = []
			for (const pawEntry of config.paws) {
				const pawConfig = normalizePawConfig(pawEntry)
				const pawPath = resolvePawPath(pawConfig.name, projectRoot)
				const manifest = await readPawManifest(pawPath)
				if (manifest) {
					paws.push({
						name: manifest.name,
						tools: manifest.tools.length,
						type: manifest.inProcess ? 'in-process' : 'subprocess',
					})
				}
			}

			if (paws.length === 0) {
				logger.info('No Paws configured')
			} else {
				logger.info('PAW                          TOOLS    TYPE')
				for (const paw of paws) {
					logger.info(`${paw.name.padEnd(29)}${String(paw.tools).padEnd(9)}${paw.type}`)
				}
			}
			break
		}

		case 'add': {
			const name = args[1]
			if (!name) {
				logger.error('Usage: vole paw add <name>')
				process.exit(1)
			}
			logger.info(`Installing ${name}...`)
			const { installPaw } = await import('./paw/install.js')
			const info = await installPaw(projectRoot, name)
			logger.info(`Added ${info.name}@${info.version} to vole.config.json`)
			if (info.listen.length > 0) {
				logger.info(`  listen ports: ${info.listen.join(', ')}`)
			}
			if (info.tools.length > 0) {
				logger.info(`  provides ${info.tools.length} tools: ${info.tools.join(', ')}`)
			}
			break
		}

		case 'remove': {
			const name = args[1]
			if (!name) {
				logger.error('Usage: vole paw remove <name>')
				process.exit(1)
			}
			const { execa: execaFn } = await import('execa')
			await execaFn('npm', ['uninstall', name], { cwd: projectRoot, stdio: 'inherit' })
			await removePawFromConfig(projectRoot, name)
			logger.info(`Removed ${name} from vole.config.json`)
			break
		}

		default:
			logger.error(`Unknown paw command: ${subcommand}`)
			logger.info('Available: list, add, remove')
			process.exit(1)
	}
}

async function handleSkillCommand(args: string[], projectRoot: string): Promise<void> {
	const subcommand = args[0]

	switch (subcommand) {
		case 'create': {
			const name = args[1]
			if (!name) {
				logger.error('Usage: vole skill create <name>')
				process.exit(1)
			}
			await scaffoldSkill(projectRoot, name)
			break
		}

		case 'list': {
			// Lightweight — load SKILL.md files without spawning paws
			const config = await (await import('./config/index.js')).loadConfig(
				path.resolve(projectRoot, 'vole.config.json'),
			)
			const { SkillRegistry } = await import('./skill/registry.js')
			const { createMessageBus } = await import('./core/bus.js')
			const { ToolRegistry } = await import('./tool/registry.js')

			const bus = createMessageBus()
			const toolRegistry = new ToolRegistry(bus)
			const skillRegistry = new SkillRegistry(bus, toolRegistry, projectRoot)

			for (const skillName of config.skills) {
				await skillRegistry.load(skillName)
			}

			const skills = skillRegistry.list()
			if (skills.length === 0) {
				logger.info('No Skills configured')
			} else {
				logger.info('SKILL                          STATUS     MISSING')
				for (const skill of skills) {
					const status = skill.active ? 'active' : 'inactive'
					const missing = skill.missingTools.length > 0 ? skill.missingTools.join(', ') : '—'
					logger.info(`${skill.name.padEnd(31)}${status.padEnd(11)}${missing}`)
				}
			}
			break
		}

		case 'add': {
			const name = args[1]
			if (!name) {
				logger.error('Usage: vole skill add <path-to-skill>')
				process.exit(1)
			}

			const skillPath = path.resolve(projectRoot, name)
			const { loadSkillFromDirectory } = await import('./skill/loader.js')
			const definition = await loadSkillFromDirectory(skillPath)

			if (!definition) {
				logger.error(`No valid SKILL.md found at ${skillPath}`)
				process.exit(1)
			}

			await addSkillToConfig(projectRoot, name)
			logger.info(`Added "${definition.name}" to vole.config.json`)
			if (definition.requiredTools.length > 0) {
				logger.info(`  requires tools: ${definition.requiredTools.join(', ')}`)
			}
			if (definition.requires?.env.length) {
				logger.info(`  requires env: ${definition.requires.env.join(', ')}`)
			}
			break
		}

		case 'remove': {
			const name = args[1]
			if (!name) {
				logger.error('Usage: vole skill remove <name>')
				process.exit(1)
			}
			await removeSkillFromConfig(projectRoot, name)

			// Delete from .openvole/skills/
			const fsModule = await import('node:fs/promises')
			const skillPath =
				name.startsWith('.') || name.startsWith('/')
					? path.resolve(projectRoot, name)
					: path.resolve(projectRoot, '.openvole', 'skills', name)
			try {
				await fsModule.rm(skillPath, { recursive: true })
				logger.info(`Deleted ${skillPath}`)
			} catch {
				// Directory may not exist — that's fine
			}

			logger.info(`Removed "${name}" from vole.config.json`)
			break
		}

		case 'search': {
			const query = args.slice(1).join(' ')
			if (!query) {
				logger.error('Usage: vole skill search <query>')
				process.exit(1)
			}
			const { VoleHubClient } = await import('./skill/volehub.js')
			const hub = new VoleHubClient()
			try {
				const results = await hub.search(query)
				if (results.length === 0) {
					logger.info('No skills found.')
				} else {
					logger.info(`Found ${results.length} skill(s):`)
					for (const skill of results) {
						logger.info(`  ${skill.name}@${skill.version} — ${skill.description}`)
						if (skill.tags.length > 0) {
							logger.info(`    tags: ${skill.tags.join(', ')}`)
						}
						if (skill.requiredTools.length > 0) {
							logger.info(`    requires: ${skill.requiredTools.join(', ')}`)
						}
					}
				}
			} catch (err) {
				logger.error(`VoleHub search failed: ${err instanceof Error ? err.message : String(err)}`)
			}
			break
		}

		case 'install': {
			const skillName = args[1]
			if (!skillName) {
				logger.error('Usage: vole skill install <name>')
				process.exit(1)
			}
			const { VoleHubClient: HubClient } = await import('./skill/volehub.js')
			const hubClient = new HubClient()
			try {
				const result = await hubClient.install(skillName, projectRoot)
				logger.info(`Installed ${result.skill.name}@${result.skill.version} to ${result.path}`)
				// Add to config
				await addSkillToConfig(projectRoot, `volehub/${result.skill.name}`)
				logger.info(`Added "volehub/${result.skill.name}" to vole.config.json`)
			} catch (err) {
				logger.error(`VoleHub install failed: ${err instanceof Error ? err.message : String(err)}`)
			}
			break
		}

		case 'uninstall': {
			const uninstallName = args[1]
			if (!uninstallName) {
				logger.error('Usage: vole skill uninstall <name>')
				process.exit(1)
			}
			const { VoleHubClient: UninstallClient } = await import('./skill/volehub.js')
			const uninstallClient = new UninstallClient()
			const removed = await uninstallClient.uninstall(uninstallName, projectRoot)
			if (removed) {
				await removeSkillFromConfig(projectRoot, `volehub/${uninstallName}`)
				logger.info(`Uninstalled "${uninstallName}" and removed from config`)
			} else {
				logger.error(`Skill "${uninstallName}" not found in VoleHub installations`)
			}
			break
		}

		case 'publish': {
			const publishPath = args[1] ?? '.'
			const { VoleHubClient: PubClient } = await import('./skill/volehub.js')
			const pubClient = new PubClient()
			try {
				const skillPath = path.resolve(projectRoot, publishPath)
				const prepared = await pubClient.preparePublish(skillPath)
				logger.info(`Skill ready for publishing:`)
				logger.info(`  name: ${prepared.name}`)
				logger.info(`  version: ${prepared.version}`)
				logger.info(`  description: ${prepared.description}`)
				logger.info(`  hash: ${prepared.contentHash}`)
				logger.info(
					`  files: ${prepared.files.length} (${prepared.files.map((f) => f.path).join(', ')})`,
				)
				logger.info(`  tools: ${prepared.requiredTools.join(', ') || 'none'}`)
				logger.info(`  tags: ${prepared.tags.join(', ') || 'none'}`)
				logger.info('')
				logger.info('To publish, create a PR against https://github.com/openvole/volehub')
				logger.info(`with all files under skills/${prepared.name}/, and add the entry (with the`)
				logger.info('"files" manifest above) to INDEX.json so the installer fetches every file.')
			} catch (err) {
				logger.error(
					`Publish preparation failed: ${err instanceof Error ? err.message : String(err)}`,
				)
			}
			break
		}

		case 'hub': {
			const { VoleHubClient: ListClient } = await import('./skill/volehub.js')
			const listClient = new ListClient()
			const installed = await listClient.listInstalled(projectRoot)
			if (installed.length === 0) {
				logger.info('No VoleHub skills installed.')
			} else {
				logger.info(`VoleHub skills (${installed.length}):`)
				for (const skill of installed) {
					logger.info(`  ${skill.name}@${skill.version} (installed: ${skill.installedAt})`)
				}
			}
			break
		}

		default:
			logger.error(`Unknown skill command: ${subcommand}`)
			logger.info('Available: list, create, add, remove, search, install, uninstall, publish, hub')
			process.exit(1)
	}
}

async function handleToolCommand(args: string[], projectRoot: string): Promise<void> {
	const subcommand = args[0]

	switch (subcommand) {
		case 'list': {
			const isLive = args.includes('--live')

			if (isLive) {
				// Live mode — boot full engine (headless) to discover all tools including MCP
				const { setLoggerSilent } = await import('./core/logger.js')
				setLoggerSilent(true)

				const engine = await createEngine(projectRoot, { headless: true })
				await engine.start()

				// Wait for late tool registration (MCP servers)
				await new Promise((resolve) => setTimeout(resolve, 3000))

				setLoggerSilent(false)

				const tools = engine.toolRegistry.list()
				if (tools.length === 0) {
					console.log('No tools registered')
				} else {
					console.log(`${tools.length} tools:\n`)
					console.log('TOOL                              PAW                              TYPE')
					for (const tool of tools) {
						console.log(
							`${tool.name.padEnd(34)}${tool.pawName.padEnd(33)}${tool.inProcess ? 'in-process' : 'subprocess'}`,
						)
					}
				}

				await engine.shutdown()
			} else {
				// Lightweight mode — no paw spawning, no heartbeat
				// Shows core tools + tools declared in paw manifests
				const config = await (await import('./config/index.js')).loadConfig(
					path.resolve(projectRoot, 'vole.config.json'),
				)

				const tools: Array<{ name: string; pawName: string; type: string }> = []

				// Core tools
				const { createMessageBus } = await import('./core/bus.js')
				const { ToolRegistry } = await import('./tool/registry.js')
				const { SchedulerStore } = await import('./core/scheduler.js')
				const { TaskQueue } = await import('./core/task.js')
				const { SkillRegistry } = await import('./skill/registry.js')
				const { createCoreTools } = await import('./tool/core-tools.js')
				const { Vault } = await import('./core/vault.js')

				const bus = createMessageBus()
				const toolRegistry = new ToolRegistry(bus)
				const skillRegistry = new SkillRegistry(bus, toolRegistry, projectRoot)
				const taskQueue = new TaskQueue(bus, 1)
				const scheduler = new SchedulerStore()
				const vault = new Vault(
					path.resolve(projectRoot, '.openvole', 'vault.json'),
					process.env.VOLE_VAULT_KEY,
				)
				await vault.init()
				const coreTools = createCoreTools(scheduler, taskQueue, projectRoot, skillRegistry, vault)
				toolRegistry.register('__core__', coreTools, true)

				for (const entry of toolRegistry.list()) {
					tools.push({ name: entry.name, pawName: entry.pawName, type: 'in-process' })
				}

				// Read paw manifests (without spawning) to get declared tools
				const { normalizePawConfig } = await import('./config/index.js')
				const { readPawManifest, resolvePawPath } = await import('./paw/manifest.js')
				for (const pawEntry of config.paws) {
					const pawConfig = normalizePawConfig(pawEntry)
					const pawPath = resolvePawPath(pawConfig.name, projectRoot)
					const manifest = await readPawManifest(pawPath)
					if (manifest?.tools) {
						for (const t of manifest.tools) {
							tools.push({
								name: t.name,
								pawName: pawConfig.name,
								type: manifest.inProcess ? 'in-process' : 'subprocess',
							})
						}
					}
				}

				if (tools.length === 0) {
					console.log('No tools registered')
				} else {
					console.log(`${tools.length} tools:\n`)
					console.log('TOOL                              PAW                              TYPE')
					for (const tool of tools) {
						console.log(`${tool.name.padEnd(34)}${tool.pawName.padEnd(33)}${tool.type}`)
					}
				}
			}
			break
		}

		case 'call': {
			const toolName = args[1]
			const paramsJson = args[2]

			if (!toolName) {
				logger.error('Usage: vole tool call <tool-name> [json-params]')
				process.exit(1)
			}

			let params: unknown = {}
			if (paramsJson) {
				try {
					params = JSON.parse(paramsJson)
				} catch {
					logger.error('Invalid JSON params')
					process.exit(1)
				}
			}

			// Lightweight boot — only register core tools (no paw spawning)
			const { createMessageBus } = await import('./core/bus.js')
			const { ToolRegistry } = await import('./tool/registry.js')
			const { SchedulerStore } = await import('./core/scheduler.js')
			const { TaskQueue } = await import('./core/task.js')
			const { SkillRegistry } = await import('./skill/registry.js')
			const { Vault } = await import('./core/vault.js')
			const { createCoreTools } = await import('./tool/core-tools.js')

			const bus = createMessageBus()
			const toolRegistry = new ToolRegistry(bus)
			const skillRegistry = new SkillRegistry(bus, toolRegistry, projectRoot)
			const taskQueue = new TaskQueue(bus, 1)
			const scheduler = new SchedulerStore()
			scheduler.setPersistence(path.resolve(projectRoot, '.openvole', 'schedules.json'))
			await scheduler.loadFromDisk()
			const vault = new Vault(
				path.resolve(projectRoot, '.openvole', 'vault.json'),
				process.env.VOLE_VAULT_KEY,
			)
			await vault.init()
			const coreTools = createCoreTools(scheduler, taskQueue, projectRoot, skillRegistry, vault)
			toolRegistry.register('__core__', coreTools, true)

			// For VoleNet tools, query the running instance via HTTP
			const config = await (await import('./config/index.js')).loadConfig(
				path.resolve(projectRoot, 'vole.config.json'),
			)
			const netConfig = config.net
			if (
				netConfig?.enabled &&
				(toolName === 'list_instances' || toolName === 'spawn_remote_agent')
			) {
				try {
					const port = netConfig.port ?? 9700
					const resp = await fetch(`http://localhost:${port}/volenet/info`, {
						signal: AbortSignal.timeout(3000),
					})
					if (resp.ok) {
						const info = await resp.json()
						// For list_instances, query the running instance directly
						if (toolName === 'list_instances') {
							console.log(JSON.stringify(info, null, 2))
							process.exit(0)
						}
					}
				} catch {
					logger.warn(
						'VoleNet instance not reachable — is an agent running? (vole serve, or vole agent start)',
					)
				}
			}

			const tool = toolRegistry.get(toolName)
			if (!tool) {
				logger.error(`Tool "${toolName}" not found in core tools`)
				logger.info(
					'Core tools only — paw tools require a running agent (vole serve, or vole agent start)',
				)
				process.exit(1)
			}

			try {
				if (tool.parameters && typeof tool.parameters.parse === 'function') {
					tool.parameters.parse(params)
				}
				const result = await tool.execute(params)
				console.log(JSON.stringify(result, null, 2))
			} catch (err) {
				logger.error(`Tool execution failed: ${err instanceof Error ? err.message : err}`)
				process.exit(1)
			}

			process.exit(0)
		}

		default:
			logger.error(`Unknown tool command: ${subcommand}`)
			logger.info('Available: list, call')
			process.exit(1)
	}
}

/**
 * Open this agent's project stores straight from disk.
 *
 * Deliberately not routed through a running engine: projects and their task queues are files, so
 * `vole project list` works on a stopped agent — which is exactly when you want to look.
 */
async function openProjectStores(projectRoot: string) {
	const { ProjectStore } = await import('./project/store.js')
	const { TaskStore } = await import('./project/tasks.js')
	const { loadConfig } = await import('./config/index.js')

	let allowedPaths: string[] = []
	try {
		const config = await loadConfig(path.resolve(projectRoot, 'vole.config.json'))
		allowedPaths = config.security?.allowedPaths ?? []
	} catch {
		// No config (or an unreadable one) only limits external roots, not listing.
	}

	const projects = new ProjectStore(path.resolve(projectRoot, '.openvole', 'workspace'), {
		agentRoot: projectRoot,
		allowedPaths,
	})
	await projects.init()
	return { projects, tasks: new TaskStore(projects), allowedPaths }
}

/** Collect repeated `--flag value` occurrences. */
function flagValues(args: string[], flag: string): string[] {
	const out: string[] = []
	for (let i = 0; i < args.length; i++) {
		if (args[i] === flag && args[i + 1]) out.push(args[++i])
	}
	return out
}

function flagValue(args: string[], flag: string): string | undefined {
	return flagValues(args, flag)[0]
}

async function handleProjectCommand(args: string[], projectRoot: string): Promise<void> {
	const subcommand = args[0]
	const { projects, tasks, allowedPaths } = await openProjectStores(projectRoot)

	switch (subcommand) {
		case 'list': {
			const status = args.includes('--all') ? ('all' as const) : undefined
			const list = await projects.list(status ? { status } : undefined)
			if (list.length === 0) {
				logger.info('No projects yet. Create one with: vole project create <id> [--root <path>]')
				break
			}
			for (const p of list) {
				const where = p.root ?? '(self-contained)'
				const open = (await tasks.list({ projectId: p.id })).length
				logger.info(`${p.id}  [${p.kind}/${p.status}]  ${open} open  ${where}`)
			}
			break
		}

		case 'scan': {
			const root = args[1]
			if (!root) {
				logger.error('Usage: vole project scan <path>')
				process.exit(1)
			}
			const { scanProjectRoot } = await import('./project/scan.js')
			try {
				const result = await scanProjectRoot(root, allowedPaths)
				logger.info(result.summary)
				logger.info(`  root:  ${result.root}`)
				logger.info(`  kind:  ${result.kind}`)
				if (result.stack.length) logger.info(`  stack: ${result.stack.join(', ')}`)
				if (result.readFirst.length) logger.info(`  docs:  ${result.readFirst.join(', ')}`)
				for (const t of result.suggestedTasks) logger.info(`  task?  ${t}`)
			} catch (err) {
				logger.error(err instanceof Error ? err.message : String(err))
				process.exit(1)
			}
			break
		}

		case 'create': {
			const id = args[1]
			if (!id) {
				logger.error(
					'Usage: vole project create <id> [--name <name>] [--kind <kind>] [--root <path>]',
				)
				process.exit(1)
			}
			try {
				const created = await projects.create({
					id,
					name: flagValue(args, '--name'),
					kind: flagValue(args, '--kind') as never,
					root: flagValue(args, '--root'),
				})
				logger.info(`Created project "${created.id}" at ${projects.dirFor(created.id)}`)
				if (created.root) logger.info(`  files: ${created.root}`)
			} catch (err) {
				logger.error(err instanceof Error ? err.message : String(err))
				process.exit(1)
			}
			break
		}

		case 'open': {
			const id = args[1]
			if (!id) {
				logger.error('Usage: vole project open <id>')
				process.exit(1)
			}
			const manifest = await projects.get(id)
			if (!manifest) {
				logger.error(`No such project: ${id}`)
				process.exit(1)
			}
			logger.info(`${manifest.name} [${manifest.kind}/${manifest.status}]`)
			logger.info(`  folder: ${projects.dirFor(id)}`)
			logger.info(`  files:  ${manifest.root ?? '(self-contained)'}`)
			if (manifest.stack?.length) logger.info(`  stack:  ${manifest.stack.join(', ')}`)
			const docs = await projects.readContextFiles(id)
			if (docs.inlined.length === 0)
				logger.info('  (no context docs yet — VOLE.md is the one to write)')
			for (const doc of docs.inlined) logger.info(`\n--- ${doc.name} ---\n${doc.body.trim()}\n`)
			const open = await tasks.list({ projectId: id })
			if (open.length === 0) logger.info('  no open tasks')
			for (const t of open) logger.info(`  [${t.state}] ${t.id}  ${t.goal}`)
			break
		}

		case 'archive': {
			const id = args[1]
			if (!id) {
				logger.error('Usage: vole project archive <id>')
				process.exit(1)
			}
			try {
				await projects.archive(id)
				logger.info(`Archived "${id}" — files and task history are kept.`)
			} catch (err) {
				logger.error(err instanceof Error ? err.message : String(err))
				process.exit(1)
			}
			break
		}

		default:
			logger.error(`Unknown project command: ${subcommand ?? '(none)'}`)
			logger.info('Available: list, scan, create, open, archive')
			process.exit(1)
	}
}

/**
 * Durable work items inside projects — not the in-memory run queue this command used to stub out
 * (which only ever printed "requires a running vole instance"). These live in tasks.jsonl, so they
 * are readable and editable with the agent stopped.
 */
async function handleTaskCommand(args: string[], projectRoot: string): Promise<void> {
	const subcommand = args[0]
	const { projects, tasks } = await openProjectStores(projectRoot)

	const show = (t: { state: string; id: string; goal: string; projectId: string }) =>
		logger.info(`[${t.state}] ${t.projectId}/${t.id}  ${t.goal}`)

	switch (subcommand) {
		case 'list': {
			const projectId = args[1] && !args[1].startsWith('--') ? args[1] : undefined
			const state = flagValue(args, '--state') as never
			const list = await tasks.list({ projectId, state })
			if (list.length === 0) {
				logger.info('No matching tasks.')
				break
			}
			for (const t of list) show(t)
			break
		}

		case 'add': {
			const projectId = args[1]
			const goal = args.slice(2).filter((a, i, arr) => {
				if (a.startsWith('--')) return false
				return !(i > 0 && arr[i - 1].startsWith('--'))
			})
			if (!projectId || goal.length === 0) {
				logger.error(
					'Usage: vole task add <projectId> <goal...> [--criteria "..."] [--priority <n>]',
				)
				process.exit(1)
			}
			try {
				const priority = Number(flagValue(args, '--priority') ?? 0)
				const task = await tasks.create({
					projectId,
					goal: goal.join(' '),
					doneCriteria: flagValues(args, '--criteria'),
					priority: Number.isFinite(priority) ? priority : 0,
				})
				logger.info(`Added ${task.projectId}/${task.id}: ${task.goal}`)
				if (task.doneCriteria.length === 0) {
					logger.warn('No done-criteria — the agent cannot verify this task before finishing it.')
				}
			} catch (err) {
				logger.error(err instanceof Error ? err.message : String(err))
				process.exit(1)
			}
			break
		}

		case 'next': {
			const task = await tasks.next(args[1])
			if (!task) {
				logger.info('No queued tasks.')
				break
			}
			show(task)
			for (const c of task.doneCriteria) logger.info(`   done when: ${c}`)
			break
		}

		case 'update':
		case 'cancel': {
			const projectId = args[1]
			const taskId = args[2]
			if (!projectId || !taskId) {
				logger.error(
					subcommand === 'cancel'
						? 'Usage: vole task cancel <projectId> <taskId>'
						: 'Usage: vole task update <projectId> <taskId> --state <state> [--note "..."]',
				)
				process.exit(1)
			}
			try {
				const state = subcommand === 'cancel' ? 'cancelled' : (flagValue(args, '--state') as never)
				const note = flagValue(args, '--note')
				const updated = await tasks.update(projectId, taskId, {
					...(state ? { state } : {}),
					...(note ? { note } : {}),
				})
				show(updated)
			} catch (err) {
				logger.error(err instanceof Error ? err.message : String(err))
				process.exit(1)
			}
			break
		}

		default:
			logger.error(`Unknown task command: ${subcommand ?? '(none)'}`)
			logger.info('Available: list, add, next, update, cancel')
			logger.info(`Projects: ${(await projects.list()).map((p) => p.id).join(', ') || '(none)'}`)
			process.exit(1)
	}
}

/** Interactive prompt helper */
async function ask(question: string): Promise<string> {
	const rl = (await import('node:readline')).createInterface({
		input: process.stdin,
		output: process.stdout,
	})
	return new Promise((resolve) => {
		rl.question(question, (answer) => {
			rl.close()
			resolve(answer.trim())
		})
	})
}

/** Ask yes/no */
async function confirm(question: string): Promise<boolean> {
	const answer = await ask(`${question} [y/N] `)
	return answer.toLowerCase() === 'y'
}

/** Ask for a comma-separated list */
async function askList(question: string): Promise<string[]> {
	const answer = await ask(question)
	if (!answer) return []
	return answer
		.split(',')
		.map((s) => s.trim())
		.filter(Boolean)
}

interface ToolSpec {
	name: string
	description: string
}

async function scaffoldPaw(projectRoot: string, name: string): Promise<void> {
	const fs = await import('node:fs/promises')

	const pawName = name.startsWith('paw-') ? name : `paw-${name}`
	const pawDir = path.resolve(projectRoot, 'paws', pawName)

	try {
		await fs.access(pawDir)
		logger.error(`Directory paws/${pawName} already exists`)
		process.exit(1)
	} catch {
		// Doesn't exist — good
	}

	// Interactive setup
	logger.info(`\nCreating Paw: ${pawName}\n`)

	const description = await ask('Description: ')

	// Collect tools
	const tools: ToolSpec[] = []
	logger.info('\nTools are actions the agent can perform (e.g., send_email, search_docs).')
	if (await confirm('Add tools?')) {
		let addMore = true
		while (addMore) {
			const toolName = await ask('  Tool name (e.g., send_message): ')
			if (!toolName) break
			const toolDesc = await ask('  Tool description: ')
			tools.push({ name: toolName, description: toolDesc })
			addMore = await confirm('  Add another tool?')
		}
	}

	// Hooks
	logger.info('\nHooks let your Paw react to agent activity automatically.')
	const wantObserve = await confirm('Log every tool execution? (observe hook)')
	const wantPerceive = await confirm('Inject context before the agent thinks? (perceive hook)')

	// Permissions
	logger.info('\nPermissions control what this Paw can access.')
	const networkDomains = await askList(
		'Network domains (comma-separated, e.g., api.telegram.org): ',
	)
	const listenPorts = (await askList('Ports to listen on (comma-separated, e.g., 3000): '))
		.map(Number)
		.filter((n) => !Number.isNaN(n))
	const envVars = await askList('Env variables needed (comma-separated, e.g., TELEGRAM_TOKEN): ')

	logger.info('')

	// Generate files
	await fs.mkdir(path.join(pawDir, 'src'), { recursive: true })

	// vole-paw.json
	await fs.writeFile(
		path.join(pawDir, 'vole-paw.json'),
		JSON.stringify(
			{
				name: pawName,
				version: '0.1.0',
				description,
				entry: './dist/index.js',
				brain: false,
				inProcess: false,
				transport: 'ipc',
				tools: tools.map((t) => ({ name: t.name, description: t.description })),
				permissions: {
					network: networkDomains,
					listen: listenPorts,
					filesystem: [],
					env: envVars,
				},
			},
			null,
			2,
		) + '\n',
	)

	// package.json
	await fs.writeFile(
		path.join(pawDir, 'package.json'),
		JSON.stringify(
			{
				name: pawName,
				version: '0.1.0',
				description,
				type: 'module',
				main: './dist/index.js',
				scripts: {
					build: 'tsup',
					typecheck: 'tsc --noEmit',
				},
				dependencies: {
					'@openvole/paw-sdk': 'workspace:*',
				},
				devDependencies: {
					'@types/node': '^22.0.0',
					tsup: '^8.3.0',
					typescript: '^5.6.0',
				},
			},
			null,
			2,
		) + '\n',
	)

	// tsconfig.json
	await fs.writeFile(
		path.join(pawDir, 'tsconfig.json'),
		JSON.stringify(
			{
				extends: '../../tsconfig.base.json',
				compilerOptions: { outDir: './dist', rootDir: './src' },
				include: ['src/**/*.ts'],
			},
			null,
			2,
		) + '\n',
	)

	// tsup.config.ts
	await fs.writeFile(
		path.join(pawDir, 'tsup.config.ts'),
		`import { defineConfig } from 'tsup'

export default defineConfig({
\tentry: ['src/index.ts'],
\tformat: ['esm'],
\tdts: true,
\tclean: true,
\tsourcemap: true,
\ttarget: 'node20',
\tsplitting: false,
})
`,
	)

	// src/index.ts
	await fs.writeFile(
		path.join(pawDir, 'src', 'index.ts'),
		`import { definePaw } from '@openvole/paw-sdk'
import { paw } from './paw.js'

export default definePaw(paw)
`,
	)

	// src/paw.ts — generate with real tools from the interactive session
	const toolsCode =
		tools.length > 0
			? tools
					.map(
						(t) => `\t\t{
\t\t\tname: '${t.name}',
\t\t\tdescription: '${t.description.replace(/'/g, "\\'")}',
\t\t\tparameters: z.object({
\t\t\t\t// Define your parameters here
\t\t\t}),
\t\t\tasync execute(params) {
\t\t\t\t// TODO: implement ${t.name}
\t\t\t\tthrow new Error('Not implemented')
\t\t\t},
\t\t},`,
					)
					.join('\n')
			: ''

	// Generate hooks code
	let hooksCode = ''
	if (wantObserve || wantPerceive) {
		const hookParts: string[] = []
		if (wantObserve) {
			hookParts.push(`\t\tonObserve: async (result) => {
\t\t\tconst status = result.success ? 'OK' : 'FAIL'
\t\t\tconsole.log(\`[${pawName}] \${result.toolName} → \${status} (\${result.durationMs}ms)\`)
\t\t},`)
		}
		if (wantPerceive) {
			hookParts.push(`\t\tonPerceive: async (context) => {
\t\t\t// Add data to context.metadata before the agent thinks
\t\t\t// context.metadata.myData = { ... }
\t\t\treturn context
\t\t},`)
		}
		hooksCode = `\n\thooks: {\n${hookParts.join('\n')}\n\t},\n`
	}

	await fs.writeFile(
		path.join(pawDir, 'src', 'paw.ts'),
		`import { z, type PawDefinition } from '@openvole/paw-sdk'

export const paw: PawDefinition = {
\tname: '${pawName}',
\tversion: '0.1.0',
\tdescription: '${description.replace(/'/g, "\\'")}',

\ttools: [
${toolsCode}
\t],
${hooksCode}
\tasync onLoad() {
\t\tconsole.log('[${pawName}] loaded')
\t},

\tasync onUnload() {
\t\tconsole.log('[${pawName}] unloaded')
\t},
}
`,
	)

	const allow: Record<string, unknown> = {}
	if (networkDomains.length > 0) allow.network = networkDomains
	if (listenPorts.length > 0) allow.listen = listenPorts
	if (envVars.length > 0) allow.env = envVars

	logger.info(`Created paws/${pawName}/`)
	if (tools.length > 0) {
		logger.info(
			`Generated ${tools.length} tool${tools.length > 1 ? 's' : ''}: ${tools.map((t) => t.name).join(', ')}`,
		)
	}
	logger.info('')
	logger.info('Next: implement your tool logic in src/paw.ts, then build:')
	logger.info('  pnpm install && pnpm build')
}

async function scaffoldSkill(projectRoot: string, name: string): Promise<void> {
	const fs = await import('node:fs/promises')

	const skillName = name.startsWith('skill-') ? name : `skill-${name}`
	const skillDir = path.resolve(projectRoot, '.openvole', 'skills', skillName)

	try {
		await fs.access(skillDir)
		logger.error(`Skill "${skillName}" already exists`)
		process.exit(1)
	} catch {
		// Doesn't exist — good
	}

	// Interactive setup
	logger.info(`\nCreating Skill: ${skillName}\n`)

	const description = await ask('Description: ')

	logger.info(
		'\nSkills describe behavior — what the agent should do, using tools provided by Paws.',
	)
	const requiredTools = await askList(
		'Required tools (comma-separated, e.g., email_search, email_send): ',
	)
	const optionalTools = await askList('Optional tools (comma-separated, or empty): ')
	const tags = await askList('Tags (comma-separated, e.g., email, productivity): ')

	logger.info('')
	const instructions = await ask('Instructions (what should the agent do?): ')

	logger.info('')

	// Create skill directory with optional subdirectories
	await fs.mkdir(skillDir, { recursive: true })

	// Build YAML frontmatter
	const frontmatterLines = [
		`name: ${skillName}`,
		`description: "${description.replace(/"/g, '\\"')}"`,
	]

	if (requiredTools.length > 0) {
		frontmatterLines.push('requiredTools:')
		for (const t of requiredTools) {
			frontmatterLines.push(`  - ${t}`)
		}
	}

	if (optionalTools.length > 0) {
		frontmatterLines.push('optionalTools:')
		for (const t of optionalTools) {
			frontmatterLines.push(`  - ${t}`)
		}
	}

	if (tags.length > 0) {
		frontmatterLines.push('tags:')
		for (const t of tags) {
			frontmatterLines.push(`  - ${t}`)
		}
	}

	// SKILL.md
	const skillMd = `---
${frontmatterLines.join('\n')}
---

# ${skillName}

${instructions}
`

	await fs.writeFile(path.join(skillDir, 'SKILL.md'), skillMd)

	// Auto-register in config and lock file (use bare name — resolver finds it in .openvole/skills/)
	await addSkillToConfig(projectRoot, skillName)

	logger.info(`Created .openvole/skills/${skillName}/`)
	logger.info(`  SKILL.md — edit to refine instructions`)
	logger.info(`Added to vole.config.json`)
	if (requiredTools.length > 0) {
		logger.info(`Requires tools: ${requiredTools.join(', ')}`)
	}
}

async function handleClawHubCommand(args: string[], projectRoot: string): Promise<void> {
	const subcommand = args[0]

	switch (subcommand) {
		case 'install': {
			const skillName = args[1]
			if (!skillName) {
				logger.error('Usage: vole clawhub install <skill-name>')
				process.exit(1)
			}

			const fsModule = await import('node:fs/promises')
			const clawHubDir = path.resolve(projectRoot, '.openvole', 'skills', 'clawhub')
			await fsModule.mkdir(clawHubDir, { recursive: true })

			// Install into .openvole/skills/clawhub/<name>
			logger.info(`Installing "${skillName}" from ClawHub...`)
			const { execa: execaFn } = await import('execa')
			try {
				await execaFn('npx', ['clawhub', 'install', skillName, '--dir', clawHubDir], {
					cwd: projectRoot,
					stdio: 'inherit',
				})
			} catch {
				logger.error(`Failed to install "${skillName}" from ClawHub`)
				process.exit(1)
			}

			// Find the installed skill directory
			const installed = await fsModule.readdir(clawHubDir).catch(() => [] as string[])
			const skillDir = installed.find((d) => d === skillName || d.includes(skillName))

			if (!skillDir) {
				logger.error(`Skill installed but directory not found in .openvole/skills/clawhub/`)
				process.exit(1)
			}

			const localPath = `clawhub/${skillDir}`
			const { loadSkillFromDirectory } = await import('./skill/loader.js')
			const definition = await loadSkillFromDirectory(
				path.resolve(projectRoot, '.openvole', 'skills', 'clawhub', skillDir),
			)

			if (definition) {
				await addSkillToConfig(projectRoot, localPath)
				logger.info(`Added "${definition.name}" to vole.config.json`)
				if (definition.requiredTools.length > 0) {
					logger.info(`  requires tools: ${definition.requiredTools.join(', ')}`)
				}
				if (definition.requires?.env.length) {
					logger.info(`  requires env: ${definition.requires.env.join(', ')}`)
				}
			} else {
				// Check if it's an OpenClaw plugin instead of a skill
				const fsCheck = await import('node:fs/promises')
				const pluginJsonPath = path.resolve(
					projectRoot,
					'.openvole',
					'skills',
					'clawhub',
					skillDir ?? skillName,
					'openclaw.plugin.json',
				)
				try {
					await fsCheck.access(pluginJsonPath)
					logger.error(
						`"${skillName}" is an OpenClaw plugin, not a skill. OpenVole does not support OpenClaw plugins — use Paws instead.`,
					)
				} catch {
					logger.warn(
						`Installed "${skillName}" but could not parse SKILL.md — add to vole.config.json manually`,
					)
				}
			}
			break
		}

		case 'search': {
			const query = args.slice(1).join(' ')
			if (!query) {
				logger.error('Usage: vole clawhub search <query>')
				process.exit(1)
			}

			const { execa: execaFn } = await import('execa')
			try {
				await execaFn('npx', ['clawhub', 'search', query], {
					cwd: projectRoot,
					stdio: 'inherit',
				})
			} catch {
				logger.error('Search failed — make sure clawhub is available (npx clawhub)')
			}
			break
		}

		case 'remove': {
			const skillName = args[1]
			if (!skillName) {
				logger.error('Usage: vole clawhub remove <skill-name>')
				process.exit(1)
			}

			const fsModule = await import('node:fs/promises')
			const skillPath = path.resolve(projectRoot, '.openvole', 'skills', 'clawhub', skillName)

			try {
				await fsModule.rm(skillPath, { recursive: true })
				logger.info(`Deleted .openvole/skills/clawhub/${skillName}`)
			} catch {
				logger.error(`Skill directory not found: .openvole/skills/clawhub/${skillName}`)
				process.exit(1)
			}

			// Remove from config
			await removeSkillFromConfig(projectRoot, `clawhub/${skillName}`)

			logger.info(`Removed "${skillName}" from vole.config.json`)
			break
		}

		default:
			logger.error(`Unknown clawhub command: ${subcommand}`)
			logger.info('Available: install, remove, search')
			process.exit(1)
	}
}

async function handleNetCommand(args: string[], projectRoot: string): Promise<void> {
	const subcommand = args[0]
	const netDir = path.resolve(projectRoot, '.openvole', 'net')

	switch (subcommand) {
		case 'init': {
			const { generateKeyPair } = await import('./net/index.js')
			const name = args[1] ?? 'vole'
			const keyPair = await generateKeyPair(netDir, name)
			logger.info(`Keypair generated!`)
			logger.info(`Instance ID: ${keyPair.instanceId}`)
			logger.info(`Public key:  ${keyPair.publicKeyString}`)
			logger.info('')
			logger.info(`Share your public key with peers:`)
			logger.info(`  ${keyPair.publicKeyString}`)
			logger.info('')
			logger.info(`Files:`)
			logger.info(`  Private key: .openvole/net/vole_key`)
			logger.info(`  Public key:  .openvole/net/vole_key.pub`)
			logger.info(`  Trusted peers: .openvole/net/authorized_voles`)
			break
		}

		case 'show-key': {
			const { loadKeyPair } = await import('./net/index.js')
			const keyPair = await loadKeyPair(netDir)
			if (!keyPair) {
				logger.error('No keypair found. Run "vole net init" first.')
				process.exit(1)
			}
			logger.info(keyPair.publicKeyString)
			break
		}

		case 'pair': {
			// Consent-based pairing between two nodes you (or two people) operate:
			//   vole net pair <url> [--name <n>] [--note <text>] [--yes]   initiate
			//   vole net pair list | accept <ref> | deny <ref>             respond
			const sub = args[1]
			const fsp = await import('node:fs/promises')
			if (sub === 'list' || sub === 'accept' || sub === 'deny') {
				const reqPath = path.join(netDir, 'pair_requests.json')
				let requests: Array<{
					id: string
					name: string
					publicKey: string
					note?: string
					ts: number
				}> = []
				try {
					requests = JSON.parse(await fsp.readFile(reqPath, 'utf-8'))
				} catch {
					/* none */
				}
				if (sub === 'list') {
					if (requests.length === 0) {
						logger.info('No pending pair requests.')
						break
					}
					for (const r of requests) {
						logger.info(
							`${r.name}  (${r.id.substring(0, 8)})  ${new Date(r.ts).toLocaleString()}${r.note ? `  — ${r.note}` : ''}`,
						)
					}
					break
				}
				const ref = args[2]
				if (!ref) {
					logger.error(`Usage: vole net pair ${sub} <name-or-id>`)
					process.exit(1)
				}
				const idx = requests.findIndex(
					(r) => r.id === ref || r.name === ref || r.id.startsWith(ref),
				)
				if (idx < 0) {
					logger.error(`No pending pair request matching "${ref}"`)
					process.exit(1)
				}
				const req = requests[idx]
				requests.splice(idx, 1)
				await fsp.writeFile(reqPath, JSON.stringify(requests, null, 2))
				if (sub === 'accept') {
					const { trustPeer } = await import('./net/index.js')
					await trustPeer(netDir, req.publicKey)
					logger.info(`Trusted "${req.name}" (${req.id.substring(0, 8)}).`)
					logger.info('If this agent is currently running, restart it (or accept from its')
					logger.info('dashboard instead) so the running engine picks up the new trust.')
				} else {
					logger.info(`Denied pair request from "${req.name}".`)
				}
				break
			}

			const url = args[1]
			if (!url || url.startsWith('--')) {
				logger.error('Usage: vole net pair <url> [--name <name>] [--note <text>] [--yes]')
				logger.error('       vole net pair list | accept <ref> | deny <ref>')
				process.exit(1)
			}
			const base = url.replace(/\/$/, '')
			const nameIdx = args.indexOf('--name')
			const myName = nameIdx >= 0 ? args[nameIdx + 1] : path.basename(projectRoot) || 'vole'
			const noteIdx = args.indexOf('--note')
			const note = noteIdx >= 0 ? args[noteIdx + 1] : undefined
			const { loadKeyPair, generateKeyPair, trustPeer, parsePublicKey } = await import(
				'./net/index.js'
			)
			let keyPair = await loadKeyPair(netDir)
			if (!keyPair) keyPair = await generateKeyPair(netDir, myName)

			// 1. Fetch and fingerprint the peer's identity.
			let info: { publicKey?: string; name?: string }
			try {
				const r = await fetch(`${base}/volenet/info`, { signal: AbortSignal.timeout(8000) })
				info = (await r.json()) as typeof info
			} catch (err) {
				logger.error(`Could not reach ${base}: ${err instanceof Error ? err.message : String(err)}`)
				process.exit(1)
				return
			}
			if (!info.publicKey || !parsePublicKey(info.publicKey)) {
				logger.error(`${base} did not offer a public key — it may run openvole < 4.13.`)
				process.exit(1)
				return
			}
			const parsed = parsePublicKey(info.publicKey)!
			logger.info(`Peer identity: ${info.name ?? '(name not published)'}`)
			logger.info(`Fingerprint:   ${parsed.instanceId}`)
			if (!args.includes('--yes')) {
				const readline = await import('node:readline/promises')
				const rl = readline.createInterface({ input: process.stdin, output: process.stdout })
				const answer = (await rl.question('Trust this node and send a pair request? [y/N] ')).trim()
				rl.close()
				if (answer.toLowerCase() !== 'y' && answer.toLowerCase() !== 'yes') {
					logger.info('Aborted — nothing was trusted.')
					break
				}
			}

			// 2. Trust their pinned key locally + add a peers entry so we keep dialing them.
			await trustPeer(netDir, info.publicKey)
			try {
				const cfgPath = path.join(projectRoot, 'vole.config.json')
				const cfg = JSON.parse(await fsp.readFile(cfgPath, 'utf-8'))
				cfg.net = cfg.net ?? {}
				cfg.net.peers = cfg.net.peers ?? []
				if (!cfg.net.peers.some((p: { url?: string }) => p?.url === base)) {
					cfg.net.peers.push({ url: base, trust: 'full' })
					await fsp.writeFile(cfgPath, `${JSON.stringify(cfg, null, 2)}\n`)
					logger.info(`Added ${base} to net.peers (trust: full).`)
				}
			} catch {
				logger.warn('Could not update vole.config.json — add the peers entry manually.')
			}

			// 3. File the pair request for THEIR operator to accept.
			try {
				const r = await fetch(`${base}/volenet/pair`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ publicKey: keyPair.publicKeyString, name: myName, note }),
					signal: AbortSignal.timeout(8000),
				})
				const resp = (await r.json()) as {
					ok?: boolean
					pending?: boolean
					alreadyTrusted?: boolean
					error?: string
				}
				if (resp.alreadyTrusted) {
					logger.info('The peer already trusts this node — pairing is complete.')
				} else if (resp.pending) {
					logger.info('Pair request sent. Ask the other operator to accept it')
					logger.info('(dashboard VoleNet tab, or `vole net pair accept ' + myName + '`).')
					logger.info('The connection completes automatically once accepted — restart this')
					logger.info('agent if it is running so it picks up the new peer entry.')
				} else {
					logger.error(`Pair request failed: ${resp.error ?? 'unknown error'}`)
					process.exit(1)
				}
			} catch (err) {
				logger.error(`Pair request failed: ${err instanceof Error ? err.message : String(err)}`)
				process.exit(1)
			}
			break
		}

		case 'send': {
			// vole net send <file> --to <peer> [--note <text>] [--agent <name>] [--wait]
			// Talks to the RUNNING agent through the control plane's MCP endpoint (the only
			// authenticated CLI→engine tool path), so the transfer runs inside the engine.
			const file = args[1]
			const toIdx = args.indexOf('--to')
			const to = toIdx >= 0 ? args[toIdx + 1] : undefined
			if (!file || file.startsWith('--') || !to) {
				logger.error(
					'Usage: vole net send <file> --to <peer> [--note <text>] [--agent <name>] [--wait]',
				)
				process.exit(1)
			}
			const noteIdx = args.indexOf('--note')
			const note = noteIdx >= 0 ? args[noteIdx + 1] : undefined
			const agentIdx = args.indexOf('--agent')
			const agentArg = agentIdx >= 0 ? args[agentIdx + 1] : undefined
			const wait = args.includes('--wait')
			const absPath = path.resolve(process.cwd(), file)

			const fsp = await import('node:fs/promises')
			const os = await import('node:os')
			const home = process.env.VOLE_HOME ?? os.homedir()
			const tokenFile = path.join(home, '.openvole', 'dashboard-token')
			let token: string
			try {
				token = (await fsp.readFile(tokenFile, 'utf8')).trim()
			} catch {
				logger.error('No dashboard token — is `vole serve` running on this machine?')
				process.exit(1)
			}
			const port = Number(process.env.VOLE_DASHBOARD_PORT) || 3000
			const mcpCall = async (tool: string, argsObj: Record<string, unknown>) => {
				const res = await fetch(
					`http://127.0.0.1:${port}/mcp/${encodeURIComponent(agentArg ?? '')}?token=${encodeURIComponent(token)}`,
					{
						method: 'POST',
						headers: {
							'content-type': 'application/json',
							accept: 'application/json, text/event-stream',
						},
						body: JSON.stringify({
							jsonrpc: '2.0',
							id: 1,
							method: 'tools/call',
							params: { name: tool, arguments: argsObj },
						}),
						signal: AbortSignal.timeout(20_000),
					},
				)
				const text = await res.text()
				if (!res.ok) throw new Error(`MCP HTTP ${res.status}: ${text.slice(0, 200)}`)
				// Stateless MCP replies JSON (or a single SSE data: line) — parse either.
				const jsonText = text.startsWith('event:')
					? (text.split('\n').find((l) => l.startsWith('data:')) ?? '').slice(5)
					: text
				const rpc = JSON.parse(jsonText) as {
					result?: { content?: Array<{ text?: string }> }
					error?: { message?: string }
				}
				if (rpc.error) throw new Error(rpc.error.message ?? 'MCP error')
				const body = rpc.result?.content?.[0]?.text
				return body ? JSON.parse(body) : {}
			}

			if (!agentArg) {
				logger.error('Pass --agent <name> (the serve agent that should send the file).')
				process.exit(1)
			}
			const sent = (await mcpCall('net_send_file', { to, path: absPath, note })) as {
				ok?: boolean
				transferId?: string
				error?: string
			}
			if (!sent.ok || !sent.transferId) {
				logger.error(`Send failed: ${sent.error ?? 'unknown error'}`)
				process.exit(1)
			}
			logger.info(`Transfer started: ${sent.transferId}`)
			if (!wait) {
				logger.info('Track it with: vole net send --wait, or the dashboard VoleNet tab.')
				break
			}
			for (;;) {
				await new Promise((r) => setTimeout(r, 2000))
				const st = (await mcpCall('net_file_status', { transfer_id: sent.transferId })) as {
					transfer?: {
						state: string
						bytesDone: number
						size: number
						error?: string
						savedPath?: string
					}
				}
				const t = st.transfer
				if (!t) continue
				process.stdout.write(`\r${t.state} — ${t.bytesDone}/${t.size} bytes        `)
				if (['done', 'failed', 'rejected', 'cancelled', 'expired'].includes(t.state)) {
					process.stdout.write('\n')
					if (t.state !== 'done') {
						logger.error(`Transfer ${t.state}${t.error ? `: ${t.error}` : ''}`)
						process.exit(1)
					}
					logger.info('Delivered ✓')
					break
				}
			}
			break
		}

		case 'join': {
			const url = args[1]
			if (!url || url.startsWith('--')) {
				logger.error('Usage: vole net join <hub-url> [--name <name>]')
				process.exit(1)
			}
			const nameIdx = args.indexOf('--name')
			const name = nameIdx >= 0 ? args[nameIdx + 1] : path.basename(projectRoot) || 'guest'
			const { loadKeyPair, generateKeyPair, trustPeer } = await import('./net/index.js')
			let keyPair = await loadKeyPair(netDir)
			if (!keyPair) keyPair = await generateKeyPair(netDir, name)
			const base = url.replace(/\/$/, '')
			logger.info(`Joining VoleNet mesh at ${base} ...`)
			let resp: {
				ok?: boolean
				pending?: boolean
				hubPublicKey?: string
				instanceName?: string
				error?: string
				message?: string
			}
			try {
				const r = await fetch(`${base}/volenet/join`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ publicKey: keyPair.publicKeyString, name }),
				})
				const text = await r.text()
				// A node WITHOUT publicJoin answers this route with an empty 404 — turn the
				// resulting JSON-parse confusion into the actual explanation.
				if (r.status === 404 || !text.trim()) {
					logger.error(`${base} is not a public hub (publicJoin is not enabled there).`)
					logger.error('To connect two of your OWN nodes, use static trust instead:')
					logger.error('  1. On each node:  vole net show-key')
					logger.error('  2. On each node:  vole net trust "<the other node\'s key>"')
					logger.error("  3. Add the other node's URL under net.peers in vole.config.json")
					process.exit(1)
				}
				try {
					resp = JSON.parse(text) as typeof resp
				} catch {
					logger.error(`Hub replied with non-JSON (HTTP ${r.status}): ${text.slice(0, 120)}`)
					process.exit(1)
					return
				}
				if (!r.ok) {
					logger.error(`Join failed (${r.status}): ${resp?.error ?? 'unknown error'}`)
					process.exit(1)
				}
			} catch (err) {
				logger.error(`Could not reach hub: ${err instanceof Error ? err.message : String(err)}`)
				process.exit(1)
				return
			}
			if (resp.pending) {
				logger.info(
					resp.message ?? 'Join request submitted — pending approval by the hub operator.',
				)
				break
			}
			if (!resp.hubPublicKey) {
				logger.error('Hub did not return its public key.')
				process.exit(1)
			}
			await trustPeer(netDir, resp.hubPublicKey)
			const { readConfigFile, writeConfigFile } = await import('./config/index.js')
			const cfg = await readConfigFile(projectRoot)
			const net = (cfg.net ?? {}) as Record<string, unknown>
			net.enabled = true
			if (!net.instanceName) net.instanceName = name
			const { upsertPeerUrl } = await import('./net/index.js')
			const peers = (net.peers ?? []) as Array<{ url: string; trust?: string }>
			// Same hostname, new endpoint (e.g. a hub that moved behind a reverse proxy):
			// replace the old entry instead of stacking a dead duplicate beside it.
			const upserted = upsertPeerUrl(peers, base)
			net.peers = upserted.peers
			cfg.net = net
			await writeConfigFile(projectRoot, cfg)
			if (upserted.replaced) logger.info(`Replaced stale peer url ${upserted.replaced} → ${base}`)
			logger.info(`Joined! Trusted hub "${resp.instanceName ?? 'hub'}" and added it as a peer.`)
			logger.info('Start your agent with "vole serve" and you are on the mesh.')
			break
		}

		case 'trust': {
			const key = args.slice(1).join(' ')
			if (!key) {
				logger.error('Usage: vole net trust <public-key-string>')
				process.exit(1)
			}
			const { trustPeer } = await import('./net/index.js')
			const instanceId = await trustPeer(netDir, key)
			logger.info(`Trusted peer: ${instanceId.substring(0, 8)}`)
			break
		}

		case 'revoke': {
			const id = args[1]
			if (!id) {
				logger.error('Usage: vole net revoke <instance-id-or-key>')
				process.exit(1)
			}
			const { revokePeer } = await import('./net/index.js')
			const removed = await revokePeer(netDir, id)
			if (removed) {
				logger.info(`Revoked peer: ${id.substring(0, 8)}`)
			} else {
				logger.error('Peer not found in authorized_voles')
			}
			break
		}

		case 'peers': {
			const { loadAuthorizedVoles } = await import('./net/index.js')
			const peers = await loadAuthorizedVoles(netDir)
			if (peers.size === 0) {
				logger.info('No trusted peers. Use "vole net trust <key>" to add one.')
			} else {
				logger.info(`Trusted peers (${peers.size}):`)
				for (const [id, peer] of peers) {
					logger.info(`  ${peer.name} — ${id.substring(0, 8)}`)
				}
			}
			break
		}

		case 'status': {
			const { loadKeyPair, loadAuthorizedVoles } = await import('./net/index.js')
			const keyPair = await loadKeyPair(netDir)
			const peers = await loadAuthorizedVoles(netDir)

			if (!keyPair) {
				logger.info('VoleNet: not initialized (run "vole net init")')
			} else {
				logger.info(`VoleNet Status`)
				logger.info(`  Instance ID: ${keyPair.instanceId}`)
				logger.info(`  Public key:  ${keyPair.publicKeyString}`)
				logger.info(`  Trusted peers: ${peers.size}`)
				for (const [id, peer] of peers) {
					logger.info(`    ${peer.name} — ${id.substring(0, 8)}`)
				}
			}
			break
		}

		default:
			logger.error(`Unknown net command: ${subcommand}`)
			logger.info('Available: init, show-key, trust, revoke, peers, status')
			process.exit(1)
	}
}

/** A directory is an OpenVole root when it holds an agents registry (legacy spaces.json too). */
async function isVoleRoot(dir: string): Promise<boolean> {
	const fs = await import('node:fs/promises')
	for (const name of ['agents.json', 'spaces.json']) {
		try {
			await fs.access(path.join(dir, name))
			return true
		} catch {
			/* keep looking */
		}
	}
	return false
}

/**
 * Resolve the OpenVole root the same way `vole serve` does: explicit VOLE_HOME always wins,
 * else the cwd when it is already a root or is empty (ignoring incidental files).
 */
async function resolveRootDir(cwd: string): Promise<string | undefined> {
	if (process.env.VOLE_HOME) return path.resolve(process.env.VOLE_HOME)
	if (await isVoleRoot(cwd)) return cwd
	const fs = await import('node:fs/promises')
	const ignore = new Set(['.DS_Store', '.git', '.gitignore'])
	const entries = (await fs.readdir(cwd).catch(() => [] as string[])).filter((e) => !ignore.has(e))
	return entries.length === 0 ? cwd : undefined
}

async function handleAgentCommand(args: string[]): Promise<void> {
	const { setLoggerSilent } = await import('./core/logger.js')
	setLoggerSilent(false) // user-facing command — show logger output on the console
	const { AgentManager } = await import('./agent/manager.js')
	// Operate on the same root `vole serve` would resolve — not an implicit ~/.openvole.
	const root = await resolveRootDir(process.cwd())
	if (!root) {
		logger.error('vole agent: the current directory is not an OpenVole root and is not empty.')
		logger.info('cd into your root (the directory with agents.json), or set VOLE_HOME.')
		const os = await import('node:os')
		const legacy = path.join(os.homedir(), '.openvole')
		if (await isVoleRoot(legacy)) {
			logger.info(`Your existing agents live at ${legacy} — VOLE_HOME=${legacy} vole agent ...`)
		}
		process.exit(1)
	}
	logger.info(`OpenVole root: ${root}`)
	const mgr = new AgentManager({ home: root })
	const subcommand = args[0]

	try {
		await runAgentSubcommand(mgr, subcommand, args)
	} catch (err) {
		// User-facing command: one clean line, not a stack trace.
		logger.error(err instanceof Error ? err.message : String(err))
		process.exit(1)
	}
}

async function runAgentSubcommand(
	mgr: InstanceType<typeof import('./agent/manager.js')['AgentManager']>,
	subcommand: string | undefined,
	args: string[],
): Promise<void> {
	switch (subcommand) {
		case 'create': {
			const rest = args.slice(1)
			let customPath: string | undefined
			let orchestrator = false
			const nameParts: string[] = []
			for (let i = 0; i < rest.length; i++) {
				if (rest[i] === '--path') {
					customPath = rest[i + 1]
					i++
					continue
				}
				if (rest[i] === '--orchestrator') {
					orchestrator = true
					continue
				}
				nameParts.push(rest[i])
			}
			const name = nameParts.join(' ')
			if (!name) {
				logger.error('Usage: vole agent create <name> [--path <dir>] [--orchestrator]')
				process.exit(1)
			}
			const entry = await mgr.create(name, { path: customPath, orchestrator })
			logger.info(`Created agent "${entry.id}"`)
			logger.info(`  path: ${entry.path}`)
			if (entry.orchestrator) {
				logger.info(
					'  This agent is an ORCHESTRATOR — under "vole serve" it can manage its siblings.',
				)
			}
			logger.info('')
			logger.info(`Next: vole agent start ${entry.id}  (or manage all agents in: vole serve)`)
			logger.info('Tip: pre-equip new agents by setting up a template — vole agent template')
			break
		}

		case 'list':
		case 'status': {
			const target = subcommand === 'status' ? args[1] : undefined
			const agents = await mgr.status(target)
			if (agents.length === 0) {
				logger.info('No agents. Create one with "vole agent create <name>".')
				break
			}
			const reg = await mgr.readRegistry()
			for (const s of agents) {
				const active = reg.activeId === s.id ? '*' : ' '
				const state = s.state === 'running' ? `running (pid ${s.pid})` : 'stopped'
				const orch = s.orchestrator ? ' [orchestrator]' : ''
				logger.info(`${active} ${s.id.padEnd(16)} ${state.padEnd(22)} ${s.path}${orch}`)
			}
			break
		}

		case 'start': {
			const id = args[1]
			if (!id) {
				logger.error('Usage: vole agent start <name>')
				process.exit(1)
			}
			const cliPath = fileURLToPath(import.meta.url)
			const { pid, reused } = await mgr.start(id, { cliPath })
			logger.info(
				reused
					? `Agent "${id}" already running (pid ${pid})`
					: `Started agent "${id}" (pid ${pid})`,
			)
			const reg = await mgr.readRegistry()
			if (reg.agents.find((s) => s.id === id || s.name === id)?.orchestrator) {
				logger.info(
					'Note: orchestrator tools (agent_*) require "vole serve" — detached agents have no control channel.',
				)
			}
			break
		}

		case 'stop': {
			if (args[1] === '--all') {
				const n = await mgr.stopAll()
				logger.info(`Stopped ${n} agent(s)`)
				break
			}
			const id = args[1]
			if (!id) {
				logger.error('Usage: vole agent stop <name> | --all')
				process.exit(1)
			}
			const stopped = await mgr.stop(id)
			logger.info(stopped ? `Stopped agent "${id}"` : `Agent "${id}" was not running`)
			break
		}

		case 'switch': {
			const id = args[1]
			if (!id) {
				logger.error('Usage: vole agent switch <name>')
				process.exit(1)
			}
			const entry = await mgr.switchTo(id)
			logger.info(`Active agent: "${entry.id}"`)
			break
		}

		case 'rename': {
			const [id, ...rest] = args.slice(1)
			const newName = rest.join(' ').trim()
			if (!id || !newName) {
				logger.error('Usage: vole agent rename <name> <new name>')
				process.exit(1)
			}
			// Display name only — the id (directory, VOLE_AGENT_ID, MCP endpoint) is deliberately
			// left alone, so a running agent keeps working and needs no restart.
			const entry = await mgr.rename(id, newName)
			logger.info(`Renamed to "${entry.name}" (id stays "${entry.id}")`)
			break
		}

		case 'remove': {
			const id = args.slice(1).find((a) => !a.startsWith('--'))
			if (!id) {
				logger.error('Usage: vole agent remove <name> [--purge]')
				process.exit(1)
			}
			const purge = args.includes('--purge')
			await mgr.remove(id, { purge })
			logger.info(`Removed agent "${id}"${purge ? ' (files deleted)' : ''}`)
			break
		}

		case 'orchestrate': {
			const id = args[1]
			const mode = args[2] ?? 'on'
			if (!id || !['on', 'off'].includes(mode)) {
				logger.error('Usage: vole agent orchestrate <name> [on|off]')
				process.exit(1)
			}
			const entry = await mgr.setOrchestrator(id, mode === 'on')
			logger.info(`Agent "${entry.id}" orchestrator: ${mode.toUpperCase()}`)
			logger.info('Applies to agents run under "vole serve".')
			if (mode === 'on') {
				logger.info('Restart the agent (under vole serve) to register its agent_* tools.')
			} else {
				logger.info('Revocation is immediate — pending requests from it will be refused.')
			}
			break
		}

		case 'template': {
			const { path: tdir, created } = await mgr.ensureTemplate()
			if (created) {
				logger.info(`Created agent template: ${tdir}`)
				logger.info('Equip it (add paws to vole.config.json) — new agents clone it:')
				logger.info('  vole agent create <name>')
			} else {
				logger.info(`Agent template: ${tdir}`)
				logger.info('Edit its vole.config.json to change what new agents inherit.')
			}
			break
		}

		default:
			logger.error(`Unknown agent command: ${subcommand}`)
			logger.info(
				'Available: create, template, list, start, stop, status, switch, rename, remove, orchestrate',
			)
			process.exit(1)
	}
}

/**
 * Persistent control-plane process — one web server managing the agents under an OpenVole root
 * (`vole serve`). The root is resolved from VOLE_HOME, else the current directory when it is empty
 * (becomes a fresh root) or already an OpenVole root (has agents.json); otherwise it refuses, so
 * `serve` is scoped to where it's run rather than an implicit global ~/.openvole.
 */
async function runServe(projectRoot: string): Promise<void> {
	const { setLoggerSilent } = await import('./core/logger.js')
	setLoggerSilent(false)
	const fs = await import('node:fs/promises')
	const os = await import('node:os')

	const hasRegistry = isVoleRoot

	// Resolve the root to serve — VOLE_HOME, else the cwd when it's a root or empty.
	const resolved = await resolveRootDir(projectRoot)
	let home: string
	if (resolved) {
		home = resolved
	} else {
		logger.error('vole serve: the current directory is not an OpenVole root and is not empty.')
		logger.info(`  ${projectRoot}`)
		logger.info(
			'Run `vole serve` in an empty directory (it becomes a new root) or in an existing root (one that has agents.json).',
		)
		// A setup.sh sitting here almost always means this is an example template not yet initialized.
		if (
			await fs
				.access(path.join(projectRoot, 'setup.sh'))
				.then(() => true)
				.catch(() => false)
		) {
			logger.info(
				'This looks like an example — run `bash setup.sh` first (it installs paws, generates the key, and writes agents.json).',
			)
		}
		const legacy = path.join(os.homedir(), '.openvole')
		if (await hasRegistry(legacy)) {
			logger.info(`Your existing agents live at ${legacy} — manage them with:`)
			logger.info(`  cd ${legacy} && vole serve`)
			logger.info(`  (or)  VOLE_HOME=${legacy} vole serve`)
		}
		process.exit(1)
	}

	const fresh = !(await hasRegistry(home))
	const { ControlPlane } = await import('./agent/control-plane.js')
	const port = Number(process.env.VOLE_DASHBOARD_PORT) || 3000
	const host = process.env.VOLE_DASHBOARD_HOST || '0.0.0.0'
	// Session token gating the dashboard (page, WebSocket, panels). Persisted so the URL is stable.
	let token = process.env.VOLE_DASHBOARD_TOKEN
	if (!token) {
		// Kept under .openvole/ (the runtime data dir) so it never lands next to committable files.
		const tokenFile = path.join(home, '.openvole', 'dashboard-token')
		try {
			token = (await fs.readFile(tokenFile, 'utf-8')).trim()
		} catch {
			/* generate below */
		}
		if (!token) {
			const { randomBytes } = await import('node:crypto')
			token = randomBytes(24).toString('hex')
			await fs.mkdir(path.dirname(tokenFile), { recursive: true }).catch(() => {})
			await fs.writeFile(tokenFile, `${token}\n`, { mode: 0o600 }).catch(() => {})
		}
	}
	const cp = new ControlPlane({ cliPath: fileURLToPath(import.meta.url), port, home, host, token })
	cp.start()
	logger.info(`OpenVole root: ${home}${fresh ? '  (new)' : ''}`)
	const shownHost = host === '0.0.0.0' || host === '::' ? 'localhost' : host
	logger.info(`Manage your agents at http://${shownHost}:${port}/?token=${token}`)
	if (host === '0.0.0.0' || host === '::') {
		logger.info(
			`Dashboard is reachable on your network and gated by the token above. Firewall port ${port} (or set VOLE_DASHBOARD_HOST=127.0.0.1) on public servers.`,
		)
	}
	const shutdown = (): void => {
		cp.shutdown()
			.then(() => process.exit(0))
			.catch(() => process.exit(1))
	}
	process.on('SIGINT', shutdown)
	process.on('SIGTERM', shutdown)
	await new Promise<void>(() => {})
}

/** Daemon entry — runs a single agent's engine in this (subprocess) process until SIGTERM. */
async function runAgentDaemon(projectRoot: string): Promise<void> {
	const { installControlAdapter } = await import('./agent/control-adapter.js')
	let engine = await createEngine(projectRoot)
	// When spawned as an IPC child by the control plane, bridge engine ↔ parent.
	const adapter = process.send ? installControlAdapter(engine, projectRoot) : undefined

	// Orchestrator agent (flag lives in the parent's registry; env is just the spawn-time
	// signal): register the agent_* sibling-management tools. The reverse-RPC client is
	// created once per process — only the tools re-register per engine instance.
	let installOrchestrateTools: (eng: typeof engine) => void = () => {}
	// Any agent under the server can talk to a colleague; only an orchestrator can manage one.
	// Both use the same reverse-RPC channel, so the two are registered separately here and the
	// split is enforced again server-side in handleOrchestrateRequest.
	if (process.send) {
		const { createParentClient } = await import('./agent/orchestrate-client.js')
		const { AGENT_CHAT_PAW, createAgentMessageTool } = await import('./tool/agent-chat-tool.js')
		const { createOrchestrateTools } = await import('./tool/orchestrate-tools.js')
		const client = createParentClient()
		const selfId = process.env.VOLE_AGENT_ID ?? process.env.VOLE_SPACE_ID ?? ''
		const orchestrator = process.env.VOLE_ORCHESTRATOR === '1'
		installOrchestrateTools = (eng) => {
			// Two sources, because they are two different permissions. Registering them together
			// under one paw name made messaging look like orchestration to anything reading the
			// registry — including VoleNet, which decides what to lend a peer by source.
			eng.toolRegistry.register(AGENT_CHAT_PAW, [createAgentMessageTool(client.call, selfId)], true)
			eng.toolRegistry.addAlwaysVisiblePaw(AGENT_CHAT_PAW)
			if (orchestrator) {
				eng.toolRegistry.register(
					'__orchestrate__',
					createOrchestrateTools(client.call, selfId),
					true,
				)
				// Keep agent_* visible under tool horizon — an orchestrator's core job.
				eng.toolRegistry.addAlwaysVisiblePaw('__orchestrate__')
			}
		}
	}
	installOrchestrateTools(engine)

	const wireRestart = (eng: typeof engine): void => {
		eng.bus.on('engine:restart', async () => {
			logger.info('Engine restarting...')
			await eng.shutdown()
			engine = await createEngine(projectRoot)
			adapter?.rebind(engine)
			installOrchestrateTools(engine)
			wireRestart(engine)
			await engine.start()
			logger.info('Engine restarted successfully')
		})
	}
	wireRestart(engine)
	await engine.start()

	// Keep the event loop alive even if the agent has no listening paws yet
	// (a pending promise alone would let Node exit once the loop drains).
	const keepAlive = setInterval(() => {}, 1 << 30)

	const shutdown = (): void => {
		clearInterval(keepAlive)
		engine
			.shutdown()
			.then(() => process.exit(0))
			.catch(() => process.exit(1))
	}
	process.on('SIGINT', shutdown)
	process.on('SIGTERM', shutdown)

	// Park until a shutdown signal arrives (the handlers above call process.exit).
	await new Promise<void>(() => {})
}

main().catch((err) => {
	console.error('Fatal error:', err)
	process.exit(1)
})
